import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { STokenLogo } from '../components/StreamDisplay';
import './Wallet.css';

const ALL_METHODS = ['PayPal', 'Amazon Gift Card', 'Google Play', 'Visa Prepaid', 'BTC', 'ETH', 'USDC', 'XRP'];
const STREAM_RATE   = 0.00166;
const MIN_FIRST_USD = 5;
const MIN_NEXT_USD  = 10;
const WEEK_MS       = 7 * 24 * 60 * 60 * 1000;

function msToCountdown(ms) {
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// phoneStep: null | 'phone' | 'code'
export default function Wallet() {
  const [user, setUser]               = useState(null);
  const [userData, setUserData]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [method, setMethod]           = useState(null);
  const [amount, setAmount]           = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [phone, setPhone]             = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneStep, setPhoneStep]     = useState(null); // null | 'phone' | 'code'
  const [submitting, setSubmitting]   = useState(false);
  const [result, setResult]           = useState(null); // { success, message }
  const navigate = useNavigate();

  useEffect(() => {
    let unsub;
    const authUnsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) { setLoading(false); return; }
      unsub = onSnapshot(doc(db, 'users', u.uid), snap => {
        setUserData(snap.data());
        setLoading(false);
      });
    });
    return () => { authUnsub(); if (unsub) unsub(); };
  }, []);

  // Step 1: send OTP (with VOIP check server-side)
  const sendCode = useCallback(async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) return;
    setSubmitting(true);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'sendPhoneVerification');
      await fn({ phoneNumber: phone });
      setPhoneStep('code');
    } catch (e) {
      const msg = e.message || '';
      if (msg.toLowerCase().includes('voip')) {
        setResult({ success: false, message: 'VoIP numbers are not allowed. Please use a real carrier number (no Google Voice, TextFree, or similar apps).' });
      } else {
        setResult({ success: false, message: msg || 'Failed to send code. Check your number and try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }, [phone]);

  // Step 2: verify OTP
  const verifyCode = useCallback(async () => {
    if (!verificationCode || verificationCode.length < 4) return;
    setSubmitting(true);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'verifyPhoneCode');
      await fn({ phoneNumber: phone, code: verificationCode });
      setPhoneStep(null);
      setResult({ success: true, message: 'Phone verified! You can now withdraw.' });
    } catch (e) {
      setResult({ success: false, message: e.message || 'Incorrect code. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [phone, verificationCode]);

  const submitWithdrawal = useCallback(async () => {
    if (!method || !amount) return;
    setSubmitting(true);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'requestWithdrawal');
      const payload = { amountUsd: parseFloat(amount), method };
      if (method === 'PayPal') payload.paypalEmail = paypalEmail.trim();
      const res = await fn(payload);
      const { requiresReview } = res.data;
      setResult({
        success: true,
        message: requiresReview
          ? '✅ Withdrawal submitted. First withdrawals are reviewed within 24–48 hours.'
          : method === 'PayPal'
            ? '✅ PayPal payout sent! Allow 1–2 business days to appear in your account.'
            : '✅ Withdrawal submitted. Processing typically takes 1–3 business days.',
      });
      setAmount('');
      setMethod(null);
      setPaypalEmail('');
    } catch (e) {
      const code = e.message || '';
      if (code === 'PHONE_REQUIRED') {
        setPhoneStep('phone');
      } else if (code === 'PENDING_EXISTS') {
        setResult({ success: false, message: 'You already have a pending withdrawal. Wait for it to complete.' });
      } else if (code === 'NOT_ENOUGH_STREAMS') {
        setResult({ success: false, message: 'Not enough Streams for that amount.' });
      } else if (code.startsWith('COOLDOWN:')) {
        const readyAt = new Date(code.split(':')[1]);
        setResult({ success: false, message: `Next withdrawal available ${readyAt.toLocaleDateString()} at ${readyAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.` });
      } else {
        setResult({ success: false, message: code || 'Withdrawal failed. Try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }, [method, amount, paypalEmail]);

  if (loading) return (
    <div className="page wallet">
      <div className="skeleton" style={{ height: 160, borderRadius: 16 }} />
    </div>
  );

  if (!user) return (
    <div className="page wallet">
      <p style={{ color: 'var(--text-secondary)' }}>Please sign in to access your wallet.</p>
    </div>
  );

  const streams       = userData?.totalStreams || 0;
  const balance       = streams * STREAM_RATE;
  const isFirstWith   = (userData?.totalPayouts || 0) === 0;
  const minUsd        = isFirstWith ? MIN_FIRST_USD : MIN_NEXT_USD;
  const minStreams     = Math.ceil(minUsd / STREAM_RATE);
  const dollarAmt     = parseFloat(amount) || 0;
  const streamsNeeded = Math.ceil(dollarAmt / STREAM_RATE);
  const lastWith      = userData?.lastWithdrawalAt?.toDate?.();
  const cooldownMs    = lastWith ? WEEK_MS - (Date.now() - lastWith.getTime()) : 0;
  const inCooldown    = cooldownMs > 0;

  const paypalEmailValid = method !== 'PayPal' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail.trim());
  const canWithdraw = dollarAmt >= minUsd
    && streams >= streamsNeeded
    && !inCooldown
    && !userData?.pendingWithdrawal
    && method
    && userData?.phoneVerified
    && paypalEmailValid;

  // ── Phone step: enter number ──
  if (phoneStep === 'phone') return (
    <div className="page wallet">
      <div className="grid-bg" aria-hidden="true" />
      <header className="wallet__header">
        <button className="page-back-btn" onClick={() => { setPhoneStep(null); setResult(null); }}>← Back</button>
        <h1 className="wallet__title">Verify Phone</h1>
        <p className="wallet__subtitle">Step 1 of 2 — Enter your number</p>
      </header>
      <div className="card wallet__verify-card">
        <p className="wallet__verify-desc">
          We'll text you a verification code. Your number must be registered with a real carrier —
          VoIP numbers (Google Voice, TextFree, etc.) are not accepted.
        </p>
        {result && !result.success && (
          <div className="wallet__result wallet__result--error" style={{ marginBottom: 12 }}>{result.message}</div>
        )}
        <input
          className="wallet__input"
          type="tel"
          placeholder="+1 (555) 000-0000"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          style={{ paddingLeft: 'var(--sp-4)' }}
        />
        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--sp-4)' }}
          onClick={sendCode}
          disabled={submitting || phone.replace(/\D/g, '').length < 10}
        >
          {submitting ? 'Sending…' : 'Send Verification Code'}
        </button>
      </div>
    </div>
  );

  // ── Phone step: enter code ──
  if (phoneStep === 'code') return (
    <div className="page wallet">
      <div className="grid-bg" aria-hidden="true" />
      <header className="wallet__header">
        <button className="page-back-btn" onClick={() => { setPhoneStep('phone'); setVerificationCode(''); setResult(null); }}>← Back</button>
        <h1 className="wallet__title">Enter Code</h1>
        <p className="wallet__subtitle">Step 2 of 2 — Check your texts</p>
      </header>
      <div className="card wallet__verify-card">
        <p className="wallet__verify-desc">
          We sent a 6-digit code to <strong>{phone}</strong>. Enter it below.
        </p>
        {result && !result.success && (
          <div className="wallet__result wallet__result--error" style={{ marginBottom: 12 }}>{result.message}</div>
        )}
        <input
          className="wallet__input"
          type="number"
          inputMode="numeric"
          placeholder="6-digit code"
          value={verificationCode}
          onChange={e => setVerificationCode(e.target.value.slice(0, 6))}
          style={{ paddingLeft: 'var(--sp-4)', letterSpacing: 8, fontSize: 24 }}
        />
        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--sp-4)' }}
          onClick={verifyCode}
          disabled={submitting || verificationCode.length < 4}
        >
          {submitting ? 'Verifying…' : 'Verify Code'}
        </button>
        <button
          className="btn btn--ghost btn--full"
          style={{ marginTop: 'var(--sp-2)' }}
          onClick={sendCode}
          disabled={submitting}
        >
          Resend code
        </button>
      </div>
    </div>
  );

  // ── Main wallet view ──
  return (
    <div className="page wallet">
      <div className="grid-bg" aria-hidden="true" />

      <header className="wallet__header">
        <button className="page-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Home
        </button>
        <h1 className="wallet__title">Wallet</h1>
        <p className="wallet__subtitle">Withdraw your earnings</p>
      </header>

      {result && (
        <div className={`wallet__result ${result.success ? 'wallet__result--success' : 'wallet__result--error'}`}>
          {result.message}
        </div>
      )}

      {/* Balance */}
      <div className="card card--brand wallet__balance">
        <div className="section-title">Available Balance</div>
        <div className="wallet__balance-row">
          <STokenLogo size={36} />
          <div>
            <div className="wallet__balance-streams">{streams.toLocaleString()}</div>
            <div className="wallet__balance-dollar">${balance.toFixed(2)}</div>
          </div>
        </div>
        <div className="wallet__balance-rate">
          <span>1 Stream = ${STREAM_RATE.toFixed(7)}</span>
        </div>
      </div>

      <div className="wallet__disclosure">
        Streams have no guaranteed cash value. Payout rates may change at any time. StreamStack is a rewards program, not a financial product.
      </div>

      {/* Status alerts */}
      {userData?.pendingWithdrawal && (
        <div className="wallet__alert wallet__alert--warn">
          ⏳ You have a pending withdrawal being processed.
        </div>
      )}
      {inCooldown && (
        <div className="wallet__alert wallet__alert--info">
          🕐 Next withdrawal available in <strong>{msToCountdown(cooldownMs)}</strong>
        </div>
      )}
      {!userData?.phoneVerified && (
        <div className="wallet__alert wallet__alert--warn">
          📱 <button className="wallet__alert-link" onClick={() => { setResult(null); setPhoneStep('phone'); }}>Verify your phone number</button> to enable withdrawals.
        </div>
      )}

      {/* Info row */}
      <div className="wallet__info-row">
        <div className="card wallet__info-card">
          <div className="wallet__info-label">Min withdrawal</div>
          <div className="wallet__info-value">${minUsd}</div>
        </div>
        <div className="card wallet__info-card">
          <div className="wallet__info-label">Streams needed</div>
          <div className="wallet__info-value">{minStreams.toLocaleString()}</div>
        </div>
        <div className="card wallet__info-card">
          <div className="wallet__info-label">Cooldown</div>
          <div className="wallet__info-value">7 days</div>
        </div>
      </div>

      {/* Method select */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Withdrawal Method</div>
      <div className="wallet__methods">
        {ALL_METHODS.map(m => (
          <button
            key={m}
            className={`wallet__method-btn${method === m ? ' wallet__method-btn--active' : ''}`}
            onClick={() => { setMethod(m); setPaypalEmail(''); }}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Amount + PayPal email + submit */}
      {method && (
        <>
          <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Amount (USD)</div>
          <div className="wallet__input-wrap">
            <span className="wallet__input-prefix">$</span>
            <input
              type="number"
              className="wallet__input"
              placeholder={`Min $${minUsd}`}
              min={minUsd}
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </div>
          {amount && (
            <p className="wallet__stream-cost">
              = {streamsNeeded.toLocaleString()} Streams required
            </p>
          )}

          {method === 'PayPal' && (
            <>
              <div className="section-title" style={{ marginTop: 'var(--sp-4)' }}>Your PayPal Email</div>
              <input
                className="wallet__input"
                type="email"
                placeholder="paypal@example.com"
                value={paypalEmail}
                onChange={e => setPaypalEmail(e.target.value)}
                style={{ marginTop: 4 }}
              />
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                Funds will be sent to this PayPal account.
              </p>
            </>
          )}

          <button
            className="btn btn--primary btn--full"
            style={{ marginTop: 'var(--sp-4)' }}
            disabled={!canWithdraw || submitting}
            onClick={submitWithdrawal}
          >
            {submitting
              ? 'Submitting…'
              : !userData?.phoneVerified
                ? 'Verify phone to withdraw'
                : inCooldown
                  ? `Available in ${msToCountdown(cooldownMs)}`
                  : userData?.pendingWithdrawal
                    ? 'Withdrawal pending…'
                    : method === 'PayPal' && !paypalEmailValid
                      ? 'Enter your PayPal email'
                      : !canWithdraw && dollarAmt < minUsd
                        ? `Minimum $${minUsd}`
                        : !canWithdraw && streams < streamsNeeded
                          ? 'Not enough Streams'
                          : `Withdraw $${dollarAmt > 0 ? dollarAmt.toFixed(2) : '—'} via ${method}`}
          </button>
        </>
      )}
    </div>
  );
}
